import {
  OPERATIONS,
  PROPERTY_STATUSES,
  PROPERTY_TYPES,
  AREA_UNITS,
  formatMoney,
  type PropertyStatus,
  type PropertyType,
} from '@erp/shared';
import { ChevronLeft, ChevronRight, Globe, ImageOff, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { ListingBadge, StatusBadge } from '../../components/properties/badges.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button, buttonVariants } from '../../components/ui/button.tsx';
import { Combobox } from '../../components/ui/combobox.tsx';
import { Input, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { errorMessage } from '../../lib/api.ts';
import { cn } from '../../lib/cn.ts';
import { useAssignees, useDistrictSearch } from '../../lib/contacts-api.ts';
import { priceLabel, useProperties, type PropertyFilters } from '../../lib/properties-api.ts';
import { useCan } from '../../lib/session.ts';

const PAGE_SIZE = 25;
const FILTER_KEYS = [
  'q',
  'type',
  'operation',
  'status',
  'districtId',
  'priceMin',
  'priceMax',
  'bedroomsMin',
  'lotAreaMin',
  'builtAreaMin',
  'areaUnit',
  'withoutOwner',
  'assignedMembershipId',
  'exclusive',
  'listing',
  'page',
] as const;
const NUMERIC = new Set([
  'districtId',
  'priceMin',
  'priceMax',
  'bedroomsMin',
  'lotAreaMin',
  'builtAreaMin',
  'page',
]);

/** Inventory: search by code, title or zone and filter. Filters live in the URL. */
export function PropertiesPage() {
  const [params, setParams] = useSearchParams();
  const filters = Object.fromEntries(
    FILTER_KEYS.flatMap((k) => {
      const value = params.get(k);
      return value ? [[k, NUMERIC.has(k) ? Number(value) : value]] : [];
    }),
  ) as PropertyFilters;
  const page = filters.page ?? 1;
  const properties = useProperties({ ...filters, page });
  const assignees = useAssignees();
  const canDelete = useCan('records.delete');
  const canEdit = useCan('records.edit');
  const [search, setSearch] = useState(filters.q ?? '');
  const [districtQuery, setDistrictQuery] = useState(params.get('zona') ?? '');
  const districts = useDistrictSearch(districtQuery);

  const setFilters = (values: Partial<Record<(typeof FILTER_KEYS)[number] | 'zona', string>>) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(values)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        if (!('page' in values)) next.delete('page');
        return next;
      },
      { replace: true },
    );
  };
  const setFilter = (key: (typeof FILTER_KEYS)[number], value: string | undefined) => {
    setFilters({ [key]: value ?? '' });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if ((params.get('q') ?? '') !== search.trim()) setFilter('q', search.trim() || undefined);
    }, 300);
    return () => {
      clearTimeout(timer);
    };
    // setFilter changes every render; the search text is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const total = properties.data?.total ?? 0;
  const items = properties.data?.items ?? [];
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  const filtered = Object.keys(filters).length > 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Propiedades</h1>
          <p className="text-sm text-muted-foreground">
            El inventario que tu inmobiliaria vende y alquila.
          </p>
        </div>
        <div className="flex gap-2">
          {canDelete && (
            <Link to="/propiedades/papelera" className={buttonVariants({ variant: 'ghost' })}>
              <Trash2 /> Papelera
            </Link>
          )}
          {canEdit && (
            <Link to="/propiedades/nueva" className={buttonVariants({ variant: 'brand' })}>
              <Plus /> Nueva propiedad
            </Link>
          )}
        </div>
      </div>

      <div className="space-y-3 rounded-xl border border-border bg-surface p-3 sm:p-4">
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            aria-label="Buscar por código, título o zona"
            placeholder="Buscar por código, título o zona"
            className="pl-9"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            aria-label="Tipo"
            value={filters.type ?? ''}
            onChange={(e) => {
              setFilter('type', e.target.value || undefined);
            }}
          >
            <option value="">Todos los tipos</option>
            {(Object.entries(PROPERTY_TYPES) as [PropertyType, { label: string }][]).map(
              ([value, t]) => (
                <option key={value} value={value}>
                  {t.label}
                </option>
              ),
            )}
          </Select>
          <Select
            aria-label="Operación"
            value={filters.operation ?? ''}
            onChange={(e) => {
              setFilter('operation', e.target.value || undefined);
            }}
          >
            <option value="">Venta y alquiler</option>
            <option value="sale">{OPERATIONS.sale}</option>
            <option value="rent">{OPERATIONS.rent}</option>
          </Select>
          <Select
            aria-label="Estado"
            value={filters.status ?? ''}
            onChange={(e) => {
              setFilter('status', e.target.value || undefined);
            }}
          >
            <option value="">Todos los estados</option>
            {(Object.entries(PROPERTY_STATUSES) as [PropertyStatus, string][]).map(
              ([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ),
            )}
          </Select>
          <Select
            aria-label="Agente"
            value={filters.assignedMembershipId ?? ''}
            onChange={(e) => {
              setFilter('assignedMembershipId', e.target.value || undefined);
            }}
          >
            <option value="">Todos los agentes</option>
            {(assignees.data ?? []).map((m) => (
              <option key={m.membershipId} value={m.membershipId}>
                {m.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Combobox
            query={districtQuery}
            onQueryChange={(value) => {
              setDistrictQuery(value);
              if (!value) setFilters({ districtId: '', zona: '' });
            }}
            loading={districts.isFetching}
            options={(districts.data ?? []).map((d) => ({
              id: String(d.id),
              label: d.name,
              detail: `${d.municipality.name}, ${d.department.name}`,
            }))}
            onSelect={(option) => {
              setDistrictQuery(option.label);
              setFilters({ districtId: option.id, zona: option.label });
            }}
            placeholder="Distrito (Santa Tecla…)"
            ariaLabel="Distrito"
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              aria-label="Precio desde (USD)"
              placeholder="Desde $"
              value={filters.priceMin ?? ''}
              onChange={(e) => {
                setFilter('priceMin', e.target.value || undefined);
              }}
            />
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              aria-label="Precio hasta (USD)"
              placeholder="Hasta $"
              value={filters.priceMax ?? ''}
              onChange={(e) => {
                setFilter('priceMax', e.target.value || undefined);
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:col-span-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              aria-label="Terreno desde"
              placeholder="Terreno mín."
              value={filters.lotAreaMin ?? ''}
              onChange={(e) => {
                setFilter('lotAreaMin', e.target.value || undefined);
              }}
            />
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              aria-label="Construcción desde"
              placeholder="Constr. mín."
              value={filters.builtAreaMin ?? ''}
              onChange={(e) => {
                setFilter('builtAreaMin', e.target.value || undefined);
              }}
            />
            <Select
              aria-label="Unidad de las áreas"
              className="col-span-2 sm:col-span-1 sm:w-auto"
              value={filters.areaUnit ?? 'm2'}
              onChange={(e) => {
                setFilter('areaUnit', e.target.value === 'v2' ? 'v2' : undefined);
              }}
            >
              <option value="m2">m²</option>
              <option value="v2">v²</option>
            </Select>
          </div>
          <Select
            aria-label="Habitaciones"
            value={filters.bedroomsMin ?? ''}
            onChange={(e) => {
              setFilter('bedroomsMin', e.target.value || undefined);
            }}
          >
            <option value="">Habitaciones: todas</option>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n}+ habitaciones
              </option>
            ))}
          </Select>
          <Select
            aria-label="Captación"
            value={
              filters.listing ?? filters.exclusive ?? (filters.withoutOwner ? 'withoutOwner' : '')
            }
            onChange={(e) => {
              const value = e.target.value;
              setFilters({
                listing: value === 'expiring' || value === 'expired' ? value : '',
                exclusive: value === 'true' ? 'true' : '',
                withoutOwner: value === 'withoutOwner' ? 'true' : '',
              });
            }}
          >
            <option value="">Todas las captaciones</option>
            <option value="true">Solo exclusivas</option>
            <option value="expiring">Por vencer (30 días)</option>
            <option value="expired">Vencidas</option>
            <option value="withoutOwner">Sin propietario (importadas)</option>
          </Select>
        </div>
      </div>

      {properties.isPending ? (
        <Loading />
      ) : properties.error ? (
        <Alert tone="danger">{errorMessage(properties.error)}</Alert>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center">
          <p className="font-medium">
            {filtered ? 'Ninguna propiedad coincide.' : 'Todavía no hay propiedades.'}
          </p>
          <p className="text-sm text-muted-foreground">
            {filtered
              ? 'Prueba con otra búsqueda o quita filtros.'
              : 'Registra la primera captación con su propietario.'}
          </p>
        </div>
      ) : (
        <>
          <ul
            className={cn(
              'divide-y divide-border rounded-xl border border-border bg-surface',
              properties.isFetching && 'opacity-70',
            )}
            aria-busy={properties.isFetching}
          >
            {items.map((p) => (
              <li key={p.id} className="flex gap-3 p-3 sm:p-4">
                <div className="size-16 shrink-0 overflow-hidden rounded-lg bg-muted sm:h-20 sm:w-28">
                  {p.coverUrl ? (
                    <img
                      src={p.coverUrl}
                      alt=""
                      className="size-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex size-full items-center justify-center text-muted-foreground">
                      <ImageOff className="size-5" aria-hidden="true" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <Link
                      to={`/propiedades/${p.id}`}
                      className="min-w-0 font-medium text-primary hover:underline"
                    >
                      {p.title}
                    </Link>
                    <span className="font-semibold tabular-nums">
                      {priceLabel(p, (n) => formatMoney(n))}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {[
                      PROPERTY_TYPES[p.type].label,
                      [p.neighborhood, p.district.name].filter(Boolean).join(', '),
                      p.bedrooms !== null && `${String(p.bedrooms)} hab.`,
                      p.bathrooms !== null && `${String(p.bathrooms)} baños`,
                      p.builtArea !== null
                        ? `${String(p.builtArea)} ${AREA_UNITS[p.builtAreaUnit]}`
                        : p.lotArea !== null && `${String(p.lotArea)} ${AREA_UNITS[p.lotAreaUnit]}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="font-mono">{p.code}</span>
                    <StatusBadge status={p.status} />
                    {p.exclusive && <Badge tone="info">Exclusiva</Badge>}
                    {!p.hasOwner && <Badge tone="warning">Sin propietario</Badge>}
                    <ListingBadge state={p.listingState} />
                    {p.webPublished && (
                      <Badge tone="success">
                        <Globe className="mr-1 size-3" aria-hidden="true" /> En la web
                      </Badge>
                    )}
                    <span>· {p.assignedTo.name}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <nav aria-label="Páginas" className="flex items-center justify-between text-sm">
            <p className="text-muted-foreground">
              {from}–{to} de {total}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={page <= 1}
                onClick={() => {
                  setFilters({ page: String(page - 1) });
                }}
              >
                <ChevronLeft /> Anterior
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={to >= total}
                onClick={() => {
                  setFilters({ page: String(page + 1) });
                }}
              >
                Siguiente <ChevronRight />
              </Button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
}
